import type { ReactNode } from 'react';

import { ADVANCED_ORDER_TYPE_LABELS } from '@/constants/advancedOrder';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { fontStyles } from '@/styles/typography';

import { ChartTooltip, ChartTooltipRow } from '@/ui/Charts/ChartTooltip';
import type { ChartPlot } from '@/ui/Charts/types';
import { ArrowRightIcon } from '@/ui/Icons/ArrowRightIcon';

import {
  ACTIVITY_MARKER_KINDS,
  getActivityMarkerKind,
  type ActivityMarker,
  type ActivityMarkerGroup,
} from './activityMarkers';

const ROW_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const MAX_GROUP_ROWS = 5;

const tradeTypeLabel = (marker: ActivityMarker) => {
  const { orderType, limitFill } = marker;
  if (!orderType) return ACTIVITY_MARKER_KINDS[getActivityMarkerKind(marker)].label;
  const label = `${ADVANCED_ORDER_TYPE_LABELS[orderType]} order`;
  return limitFill && limitFill.fillCount > 1
    ? `${label} · Partial fill ${limitFill.fillIndex}/${limitFill.fillCount}`
    : label;
};

const Caption = ({ children }: { children: ReactNode }) => (
  <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-700' })}>
    {children}
  </span>
);

const TradeSummary = ({ marker }: { marker: ActivityMarker }) => (
  <span className="inline-flex items-center gap-1">
    {marker.sold}
    <ArrowRightIcon role="img" aria-label="to" className="shrink-0" width={12} height={12} />
    {marker.bought}
  </span>
);

type ActivityMarkerTooltipProps = {
  group: ActivityMarkerGroup;
  plot: ChartPlot;
};

/** Hover/tap detail for one execution, or a summary when markers are grouped. */
export const ActivityMarkerTooltip = ({ group, plot }: ActivityMarkerTooltipProps) => {
  const { formatFiat } = useFiatDisplay();
  const { markers, x, y } = group;

  if (markers.length === 1) {
    const [marker] = markers;

    return (
      <ChartTooltip x={x} y={y} plot={plot}>
        <ChartTooltipRow label="Trade" value={<TradeSummary marker={marker} />} />
        <ChartTooltipRow label="Type" value={tradeTypeLabel(marker)} />
        {marker.volumeUsd != null && (
          <ChartTooltipRow label="Volume" value={formatFiat(marker.volumeUsd, true)} />
        )}
        <ChartTooltipRow label="Time" value={ROW_TIMESTAMP_FORMATTER.format(marker.timestamp)} />
      </ChartTooltip>
    );
  }

  const totalVolume = markers.reduce((sum, marker) => sum + (marker.volumeUsd ?? 0), 0);
  const visibleMarkers = markers.slice(0, MAX_GROUP_ROWS);
  const hiddenCount = markers.length - visibleMarkers.length;

  return (
    <ChartTooltip x={x} y={y} plot={plot}>
      <ChartTooltipRow
        label={`${markers.length} trades`}
        value={totalVolume > 0 ? formatFiat(totalVolume, true) : ''}
      />
      {visibleMarkers.map((marker) => (
        <div key={marker.key} className="flex items-start justify-between gap-4">
          <div className="flex flex-col">
            <span className={fontStyles({ variant: 'LABEL_SM' })}>
              <TradeSummary marker={marker} />
            </span>
            <Caption>{tradeTypeLabel(marker)}</Caption>
          </div>
          <div className="flex flex-col text-right">
            {marker.volumeUsd != null && (
              <span className={fontStyles({ variant: 'LABEL_SM' })}>
                {formatFiat(marker.volumeUsd, true)}
              </span>
            )}
            <Caption>{ROW_TIMESTAMP_FORMATTER.format(marker.timestamp)}</Caption>
          </div>
        </div>
      ))}
      {hiddenCount > 0 && <Caption>+{hiddenCount} more</Caption>}
    </ChartTooltip>
  );
};
